/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl, defineMessages} from 'react-intl'
import {validatePhoneNumber, validateVatNumber} from '../../utils/form-utils'
import {useLocaleCountry} from '../../hooks/use-locale-custom'
import {validatePostalCode} from '../../utils/utils'
import {
    useCustomPreferenceWithFallback,
    useCustomPreference,
} from '../../hooks/use-custom-preference'
import useRegions from '../../custom-hooks/use-regions'

const messages = defineMessages({
    title: {defaultMessage: 'Title', id: 'use_address_fields.label.title'},
    required: {defaultMessage: 'Required', id: 'use_address_fields.error.required'},
    firstName: {defaultMessage: 'First Name', id: 'use_address_fields.label.first_name'},
    lastName: {defaultMessage: 'Last Name', id: 'use_address_fields.label.last_name'},
    phone: {defaultMessage: 'Phone', id: 'use_address_fields.label.phone'},
    country: {defaultMessage: 'Country', id: 'use_address_fields.label.country'},
    address: {defaultMessage: 'Address', id: 'use_address_fields.label.address'},
    address2: {defaultMessage: 'Address second line', id: 'use_address_fields.label.address2'},
    city: {defaultMessage: 'City', id: 'use_address_fields.label.city'},
    state: {defaultMessage: 'State', id: 'use_address_fields.label.state'},
    province: {defaultMessage: 'Province', id: 'use_address_fields.label.province'},
    zipCode: {defaultMessage: 'Zip Code', id: 'use_address_fields.label.zipCode'},
    postalCode: {defaultMessage: 'Postal Code', id: 'use_address_fields.label.postal_code'},
    stateCodeInvalid: {
        defaultMessage: 'Please enter 2-letter state/province.',
        id: 'use_address_fields.error.state_code_invalid',
    },
    preferred: {defaultMessage: 'Set as default', id: 'use_address_fields.label.preferred'},
    optional: {defaultMessage: '(Optional)', id: 'use_address_fields.label.optional'},
    maxLength: {
        defaultMessage: 'Please insert {max} characters or fewer.',
        id: 'use_address_fields.error.max_length',
    },
})

const labels = defineMessages({
    mr: {
        defaultMessage: 'Mr',
        id: 'use_address_fields.title.option.mr',
    },
    mrs: {
        defaultMessage: 'Mrs',
        id: 'use_address_fields.title.option.mrs',
    },
    miss: {
        defaultMessage: 'Miss',
        id: 'use_address_fields.title.option.miss',
    },
})

export const buildProvinceSelect = (regions, keyCountry) => {
    if (!keyCountry) return
    const provinces = []
    const subRegions = regions?.[keyCountry] || []
    subRegions.forEach((region) => {
        provinces.push({value: region.ISOCode, label: region.Name})
    })

    return provinces
}

export const extraValidateFieldNames = [
    'firstName',
    'lastName',
    'phone',
    'address1',
    'city',
    'stateCode',
    'postalCode',
    'countryCode',
]

export const extraAddressValidation = (address) => {
    const missingFields = extraValidateFieldNames.filter(
        // eslint-disable-next-line no-prototype-builtins
        (fieldName) => !address.hasOwnProperty(fieldName),
    )
    if (missingFields.length > 0) {
        //console.error('Missing required fields in address:', missingFields)
        return false
    }
    if (!validatePhoneNumber(address.phone)) {
        return false
    }

    return true
}

/**
 * A React hook that provides the field definitions for an address form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */
export default function useAddressFields({
    form: {
        control,
        formState: {errors},
        watch,
    },

    countries = [],
    variant,
    isBillingAddress = false,
    country,
    isCurrentCountryEuropean = false,
}) {
    const {formatMessage} = useIntl()

    const countryUpperCase = useLocaleCountry().toUpperCase()

    // BY is the country used only for LABEL purpose
    const currentCountry = countryUpperCase === 'BY' ? 'IT' : countryUpperCase

    const selectedCountryWatcher = watch(`countryCode`)
    const selectedCountry =
        typeof selectedCountryWatcher === 'string'
            ? selectedCountryWatcher
            : selectedCountryWatcher?.value

    const regions = useRegions()
    const fiscalCodeValidationConfig = useCustomPreferenceWithFallback('fiscalCodeValidationConfig')
    let fiscalCodeRules = {}

    if (fiscalCodeValidationConfig) {
        try {
            const config = fiscalCodeValidationConfig
            if (config && config.regex) {
                fiscalCodeRules = {
                    validate: {
                        fiscalCodeFormat: (val) =>
                            !val ||
                            new RegExp(config.regex).test(val) ||
                            config.error ||
                            formatMessage({
                                defaultMessage: 'Invalid format',
                                id: 'use_address_fields.error.fiscal_code_format',
                            }),
                    },
                }
            }
        } catch (e) {
            console.error('Invalid fiscalCodeValidationConfig:', e)
        }
    }

    const createProvincesSelectFromCountryCode = () => {
        const provinces = buildProvinceSelect(
            regions,
            isBillingAddress && selectedCountry
                ? selectedCountry
                : country
                ? country
                : currentCountry,
        )
        return provinces
    }

    const provinceOrRegions = createProvincesSelectFromCountryCode()

    const smallB2BAdditionalInputFieldConfig = useCustomPreference('smallB2BAdditionalInputField')
    var smallB2BAdditionalInputFieldValidate = {}
    if (smallB2BAdditionalInputFieldConfig) {
        try {
            let AdditionalInputFieldConfig = smallB2BAdditionalInputFieldConfig
            if (AdditionalInputFieldConfig && AdditionalInputFieldConfig.regex) {
                smallB2BAdditionalInputFieldValidate = (val) =>
                    RegExp(AdditionalInputFieldConfig.regex).test(val) ||
                    formatMessage({
                        id: 'vat_validator.SDI_input.error.sdi_field_format',
                        defaultMessage: 'Invalid additional input field format for your country',
                    })
            }
        } catch (e) {
            console.error('Invalid smallB2BAdditionalInputFieldConfig:', e)
        }
    }

    const fields = {
        title: {
            name: `title`,
            label: formatMessage(messages.title),
            defaultValue: null,
            type: 'select',
            options: [
                {
                    value: 'mr',
                    label: formatMessage(labels.mr),
                },
                {
                    value: 'mrs',
                    label: formatMessage(labels.mrs),
                },
                {
                    value: 'miss',
                    label: formatMessage(labels.miss),
                },
            ],
            error: errors[`title`],
            control,
        },
        firstName: {
            name: `firstName`,
            label: formatMessage(messages.firstName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'given-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_address_fields.error.please_enter_first_name',
                }),
                maxLength: {
                    value: 40,
                    message: formatMessage(messages.maxLength, {max: 40}),
                },
                validate: {
                    notEmpty: (value) =>
                        value?.trim().length > 0 ||
                        formatMessage({
                            defaultMessage: 'Please enter your first name.',
                            id: 'use_address_fields.error.please_enter_first_name',
                        }),
                },
            },
            error: errors[`firstName`],
            control,
            dataCsMask: true,
        },
        lastName: {
            name: `lastName`,
            label: formatMessage(messages.lastName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_address_fields.error.please_enter_last_name',
                }),
                maxLength: {
                    value: 80,
                    message: formatMessage(messages.maxLength, {max: 80}),
                },
                validate: {
                    notEmpty: (value) =>
                        value?.trim().length > 0 ||
                        formatMessage({
                            defaultMessage: 'Please enter your last name.',
                            id: 'use_address_fields.error.please_enter_last_name',
                        }),
                },
            },
            error: errors[`lastName`],
            control,
            dataCsMask: true,
        },
        phone: {
            name: `phone`,
            label: formatMessage(messages.phone),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_address_fields.error.please_enter_phone_number',
                }),
                validate: {
                    validPhone: (value) =>
                        validatePhoneNumber(value) ||
                        formatMessage({
                            defaultMessage: 'Please enter your phone number.',
                            id: 'use_address_fields.error.please_enter_phone_number',
                        }),
                },
            },
            error: errors[`phone`],
            control,
            dataCsMask: true,
        },
        address1: {
            name: `address1`,
            label: formatMessage(messages.address),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address1',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your address.',
                    id: 'use_address_fields.error.please_select_your_address',
                }),
                validate: {
                    notEmpty: (value) =>
                        value?.trim().length > 0 ||
                        formatMessage({
                            defaultMessage: 'Please enter your address.',
                            id: 'use_address_fields.error.please_select_your_address',
                        }),
                },
            },
            error: errors[`address1`],
            control,
            dataCsMask: true,
        },
        address2: {
            name: `address2`,
            label: formatMessage(messages.address2),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address2',
            error: errors[`address2`],
            control,
            dataCsMask: true,
        },
        addressNumber: {
            name: `addressNumber`,
            label: formatMessage({
                defaultMessage: 'Address Number',
                id: 'use_address_fields.label.address_number',
            }),
            defaultValue: '',
            type: 'text',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your address number.',
                    id: 'use_address_fields.error.please_select_your_address_number',
                }),
                validate: {
                    notEmpty: (value) =>
                        value?.trim().length > 0 ||
                        formatMessage({
                            defaultMessage: 'Please enter your address number.',
                            id: 'use_address_fields.error.please_select_your_address_number',
                        }),
                },
            },
            autoComplete: 'address-line2',
            error: errors[`addressNumber`],
            control,
            dataCsMask: true,
        },
        city: {
            name: `city`,
            label: formatMessage(messages.city),
            defaultValue: '',
            type: 'text',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your city.',
                    id: 'use_address_fields.error.please_select_your_city',
                }),
                maxLength: {
                    value: 40,
                    message: formatMessage(messages.maxLength, {max: 40}),
                },
                validate: {
                    notEmpty: (value) =>
                        value?.trim().length > 0 ||
                        formatMessage({
                            defaultMessage: 'Please enter your city.',
                            id: 'use_address_fields.error.please_select_your_city',
                        }),
                },
            },
            error: errors[`city`],
            control,
            dataCsMask: true,
        },
        stateCode: {
            name: `stateCode`,
            label: formatMessage(messages.state),
            defaultValue: '',
            type: provinceOrRegions.length > 0 ? 'select' : 'text',
            options: provinceOrRegions,
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your state.',
                    id: 'use_address_fields.error.please_select_your_state',
                }),
                ...(provinceOrRegions.length === 0 && {
                    validate: {
                        notEmpty: (value) =>
                            value?.trim().length > 0 ||
                            formatMessage({
                                defaultMessage: 'Please enter your state.',
                                id: 'use_address_fields.error.please_select_your_state',
                            }),
                    },
                }),
            },
            error: errors[`stateCode`],
            control,
            dataCsMask: true,
        },
        postalCode: {
            name: `postalCode`,
            label: formatMessage(messages.postalCode),
            defaultValue: '',
            type: 'text',
            autoComplete: 'postal-code',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your postal code.',
                    id: 'use_address_fields.error.please_enter_your_postal_or_zip',
                }),
                validate: {
                    notEmpty: (value) =>
                        value?.trim().length > 0 ||
                        formatMessage({
                            defaultMessage: 'Please enter your postal code.',
                            id: 'use_address_fields.error.please_enter_your_postal_or_zip',
                        }),
                    validPostalCode: (value) => {
                        const isValid = validatePostalCode(selectedCountry || currentCountry, value)
                        return (
                            isValid ||
                            formatMessage({
                                defaultMessage: 'Invalid postal code.',
                                id: 'use_address_fields.error.invalid_postal_code',
                            })
                        )
                    },
                },
            },
            error: errors[`postalCode`],
            control,
            dataCsMask: true,
        },
        countryCode: {
            name: `countryCode`,
            label: formatMessage(messages.country),
            defaultValue: countries.find((country) => country.value === currentCountry),
            type: 'select',
            options: countries,
            inputProps: {
                isDisabled: isBillingAddress && isCurrentCountryEuropean ? false : true,
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select your country.',
                    id: 'use_address_fields.error.please_select_your_country',
                }),
                validate: {
                    objectEmpty: (obj) =>
                        (obj && obj.value !== '') ||
                        formatMessage({
                            defaultMessage: 'Please select your country.',
                            id: 'use_address_fields.error.please_select_your_country',
                        }),
                },
            },
            error: errors[`countryCode`],
            control,
            dataCsMask: true,
        },
        addressLookup: {
            name: `addressLookup`,
            type: 'addressLookup',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select a billing address',
                    id: 'use_address_fields.error.please_select_billing_address',
                }),
            },
            validate: {
                required: (value) => !!value,
            },
            error: errors[`addressLookup`],
            control,
        },
        preferred: {
            name: `c_isPreferredAddress`,
            label: formatMessage(messages.preferred),
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            variant,
            rules: {},
            control,
        },
        fiscalCodeNumber: {
            name: `c_fiscalCode`,
            control,
            label: formatMessage({
                id: 'vat_validator.tin_input.default_value',
                defaultMessage: 'TIN number',
            }),
            type: 'text',
            defaultValue: '',
            rules: fiscalCodeRules,
            error: errors[`c_fiscalCode`],
            dataCsMask: true,
        },
        vatNumber: {
            name: `c_vatNumber`,
            control,
            label: formatMessage({
                id: 'vat_validator.vat_input.default_value',
                defaultMessage: 'VAT number',
            }),
            type: 'text',
            defaultValue: '',
            error: errors[`c_vatNumber`],
            rules: {
                required: formatMessage({
                    id: 'vat_validator.vat_input_error.vat_number',
                    defaultMessage: 'Please insert your VAT number',
                }),
                validate: {
                    countryVatFormat: (val) => {
                        const countryCode = (selectedCountry || currentCountry).toUpperCase()
                        const {vatNumber} = validateVatNumber(val, countryCode)
                        return (
                            vatNumber ||
                            formatMessage({
                                id: 'vat_validator.vat_input.error.vat_format',
                                defaultMessage: 'Invalid VAT number format for selected country.',
                            })
                        )
                    },
                },
                dataCsMask: true,
            },
        },
        companyName: {
            name: `c_companyName`,
            control: control,
            defaultValue: '',
            label: formatMessage({
                id: 'vat_validator.company_name_input.default_value',
                defaultMessage: 'Company name',
            }),
            rules: {
                required: formatMessage({
                    id: 'vat_validator.vat_input_error.company_name',
                    defaultMessage: 'Please insert your company name',
                }),
                validate: {
                    notEmpty: (value) =>
                        value?.trim().length > 0 ||
                        formatMessage({
                            id: 'vat_validator.vat_input_error.company_name',
                            defaultMessage: 'Please insert your company name',
                        }),
                },
            },
            error: errors[`c_companyName`],
        },
        sdi: {
            name: `c_sdi`,
            control: control,
            defaultValue: '',
            label: formatMessage({
                id: 'vat_validator.SDI_input.default_value',
                defaultMessage: 'SDI',
            }),
            rules: {
                required: formatMessage({
                    id: 'vat_validator.vat_input_error.sdi_number',
                    defaultMessage: 'Please insert your sdi number',
                }),
                validate: smallB2BAdditionalInputFieldValidate,
            },
            error: errors[`c_sdi`],
            dataCsMask: true,
        },
        vatConfirmed: {
            name: `c_vatNumberVerified`,
            defaultValue: false,
            type: 'checkbox',
            rules: {},
            control,
        },
    }

    return fields
}
